import VectorSource from 'ol/source/Vector';
import VectorEventType from 'ol/source/VectorEventType';
import Vector from 'ol/layer/Vector';
import { MapProperty } from 'cadenza/map/map-property';
import { unByKey } from 'ol/Observable';
import { logger } from 'cadenza/utils/logging';
import GeoJSON from 'ol/format/GeoJSON';

import { SelectContextMenu } from './select-context-menu';
import { SelectBase } from './base-feature-selection';
import { DrawInteraction } from './draw-interaction';

const DRAW_TYPE = 'Polygon';

export class PolygonFeatureSelection extends DrawInteraction {

  #isPolygonCondition;

  constructor (map, options = {}) {
    const source = new VectorSource();
    super({
      type: DRAW_TYPE,
      source,
      style: map.get(MapProperty.DRAW_STYLES)[DRAW_TYPE],
      condition: () => true, // by default draw ignores events when any modifier key is pressed, don't ignore events
      freehandCondition: () => false // by default draw goes into freehand if shift is pressed, don't go into freehand
    });
    this._selectBase = new SelectBase(map, options);
    this.#isPolygonCondition = options.isPolygonCondition;
    this._isModifierKeysAware = options.isModifierKeysAware;
    if (map.get(MapProperty.CONTEXT_MENU_ENABLED)) {
      this._selectContextMenu = new SelectContextMenu(map, this);
    }
    this.setActive(false);
    this._map = map;
    this._source = source;

    this._vectorLayer = new Vector({ map, source });

    this.on('change:active', event => {
      if (!event.oldValue) {
        this._onActivation();
      } else {
        this._onDeactivation();
      }
    });

    this._onKeyDownHandler = event => this._setPressed(event, true);
    this._onKeyUpHandler = event => this._setPressed(event, false);
    this._onAddFeatureHandler = () => this._onAddFeature();
    this._onDrawEndHandler = event => this._onDrawEnd(event);
  }

  _onActivation () {
    this._source.clear(true);
    this._modifierKeysPressed = {};
    if (this._isModifierKeysAware) {
      document.addEventListener('keydown', this._onKeyDownHandler);
      document.addEventListener('keyup', this._onKeyUpHandler);
    }
    this._eventKeys = [
      this._source.on(VectorEventType.ADDFEATURE, this._onAddFeatureHandler),
      this.on('drawend', this._onDrawEndHandler),
      this._map.on('pointermove', event => this._handleMoveEvent(event))
    ];
  }

  _onDeactivation () {
    this._vectorLayer.setMap(null);
    this._source.clear(true);
    if (this._isModifierKeysAware) {
      document.removeEventListener('keydown', this._onKeyDownHandler);
      document.removeEventListener('keyup', this._onKeyUpHandler);
    }
    unByKey(this._eventKeys);
  }

  /* The draw end event, does not contain the original mouse event which has the shift and ctrl modifiers, therefore
   * we need to somehow cache whether ctrl or shift is pressed */
  _setPressed (event, isPressed) {
    if (event.key === 'Control' || event.key === 'Shift') {
      this._modifierKeysPressed[event.key === 'Control' ? 'ctrlKey' : 'shiftKey'] = isPressed;
    }
  }

  _onAddFeature () {
    this._source.clear(true);
  }

  _onDrawEnd (event) {
    if (this.#isPolygonCondition) {
      const polygonConditionLayer = this._map.get(MapProperty.POLYGON_CONDITION_LAYER);
      const geoJSON = new GeoJSON();
      const mapSrs = this._map.getView().getProjection().getCode();
      addFeatureToPolygonConditionLayer(polygonConditionLayer, event.feature);
      const allGeometries = polygonConditionLayer.getSource().getFeatures().map(feature => ({
        geometry: geoJSON.writeGeometry(feature.getGeometry()),
        srs: mapSrs
      }));
      const polygonConditionModal = window.parent.document.querySelector('.d-polygon-condition-modal');
      polygonConditionModal.setGeometries(allGeometries);
    } else {
      this._selectBase
        .selectFeatures(this._modifierKeysPressed, event.feature)
        .then(() => {
          if (this._selectBase.hasContextMenu()) {
            this._selectContextMenu.showContextMenuIfSelectedFeaturesExist(this._lastMouseMoveEvent);
          }
        })
        .catch(error => logger.error(error));
    }
  }

  // Override the OL_DRAW method, issues with the drawing pointing circle if you don't
  abortDrawing_ () {
    this.finishCoordinate_ = null;
    const sketchFeature = this.sketchFeature_;
    this.sketchFeature_ = null;
    this.sketchPoint_ = null;
    this.sketchLine_ = null;
    this.overlay_.getSource().clear(true);
    return sketchFeature;
  }

  _handleMoveEvent (event) {
    this._lastMouseMoveEvent = event;
    if (this._selectBase.hasContextMenu()) {
      this._selectContextMenu.prepareToShowContextMenu(event);
    }
  }

}

function addFeatureToPolygonConditionLayer (polygonConditionLayer, feature) {
  const source = polygonConditionLayer.getSource();
  source.addFeature(feature);
}
